/// <reference types="resize-observer-browser" />
import { HideBaseIcon } from '@tableau/icons/HideBaseIcon';
import { ShowBaseIcon } from '@tableau/icons/ShowBaseIcon';
import { ActivitySpinnerWidget, ButtonType, ButtonWidget, DropdownButtonType, List, TextLinkWidget } from '@tableau/widgets';
import { Formik, FormikHelpers, FormikProps } from 'formik';
import React from 'react';

import { FormikSingleSelectDropdown } from '~src/commonInfra/components/FormikSingleSelectDropdown';
import { ReactModalService } from '~src/commonInfra/dialogs/ReactModalService';
import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { HelpService } from '~src/commonInfra/services/HelpService';
import { Tableau } from '~src/commonInfra/utils/TableauLog';
import { ListDialogErrorAlert } from '~src/connectivity/components/AddCredentialAuthModeDialog';
import { OAuthCredentialTypes } from '~src/connectivity/serverApi/types/OAuth/IOAuthCredential';
import {
  ACCEPTED_FILE_TYPES,
  FILE_SIZE_LIMIT,
  readFileContent,
  validateCustomOAuthConfig,
  validateNoEmbeddedWhiteSpaceCI,
  validateNoEmbeddedWhiteSpaceCS,
  validateRedirectUri,
} from '~src/connectivity/utils/oauthValidators';
import { ConnectToLLMDialogStyles } from '~src/settings/general-settings/styles/tableau-gai/ConnectToLLMDialogStyles';
import { learnMoreStyle } from '~src/settings/styles/CommonStyles';
import { FileInput } from '~src/userManagement/components/FileInput';

import { Messages } from '../../../../../codegen/Localize';
import { FormikTextBox } from '../../../../../commonInfra/components/FormikField';
import { BetaTermsDialog } from './BetaTermsDialog';
import { sendByollmTermsAcceptedTelemetry } from './ByollmTelemetry';
import { AUTH_MODES, AuthMode, PROVIDER_TYPES } from './GenerativeAITypes';

export interface ConnectToLLMSetting {
  llmProvider: string;
  providerAPIKey: string;
  azureResourceName?: string;
  azureSubscriptionId?: string;
  azureResourceGroup?: string;
  azureTenantId?: string;
  azureServicePrincipalSecret?: string;
  hostURL?: string;
  // Authentication mode for Custom Gateway (BYOLLM): None | ApiKey | OAuth
  authMode?: AuthMode;
  // OAuth fields for Custom Gateway (LLM Gateway)
  oauthConfigId?: string;
  oauthClientId?: string;
  oauthClientSecret?: string;
  oauthRedirectUri?: string;
  oauthConfigContent?: string;
  oauthConfigFileName?: string;
  configLabel?: string;
  // Custom Gateway (BYOLLM) Beta disclaimer acceptance. Session-only local form
  // state with two roles: (1) suppress the dropdown re-prompt once accepted, and
  // (2) gate submit for BYOLLM. When termsAccepted is false and llmProvider is
  // BYOLLM, handleSubmit opens the terms modal instead of proceeding. It does not
  // gate field visibility (that keys only on llmProvider). Not persisted to any
  // site setting and not read back on load.
  termsAccepted?: boolean;
}

export interface ConnectToLLMDialogProps {
  readonly dismiss: () => void;
  readonly close: (result: any) => void;
  connectToLLMSubmit: (values: ConnectToLLMSetting) => void;
  isEditing?: boolean;
  currentValues?: Partial<ConnectToLLMSetting>;
  azureAiOnPremEnabledSP2?: boolean;
  bringYourOwnLLMForOnPremEnabled?: boolean;
}

interface ConnectToLLMDialogState {
  isAPIKeyVisible: boolean;
  // OAuth file upload state
  oauthFileName?: string;
  oauthConfigContent?: string;
  customOAuthConfigId?: string;
  oauthConfigLabel?: string;
  hasOAuthFileErrors?: boolean;
  validatingOAuthFile?: boolean;
}

export class ConnectToLLMDialog extends React.Component<ConnectToLLMDialogProps, ConnectToLLMDialogState> {

  private fileInput: React.RefObject<HTMLInputElement>;
  // Ref on the form element so we can observe height changes and trigger a dialog
  // re-center when the form grows/shrinks (e.g. when auth mode changes).
  private formRef: React.RefObject<HTMLFormElement>;
  private formResizeObserver?: ResizeObserver;
  private lastObservedHeight: number = 0;

  state: ConnectToLLMDialogState = {
    isAPIKeyVisible: false,
  };

  constructor(props: ConnectToLLMDialogProps) {
    super(props);
    this.fileInput = React.createRef();
    this.formRef = React.createRef();
  }

  componentDidMount() {
    // Watch the form for size changes and ask the surrounding DialogWidget to
    // re-center itself. DialogWidget listens for window 'resize' events (it's
    // opened with shouldAdjustPositionOnWindowResize: true) and calls
    // repositionDialog() in response, which is exactly what we want when the
    // BYOLLM auth-mode dropdown toggles visible OAuth/API key fields.
    const formEl = this.formRef.current;
    if (formEl && typeof ResizeObserver !== 'undefined') {
      this.formResizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const newHeight = entry.contentRect.height;
          if (newHeight !== this.lastObservedHeight) {
            this.lastObservedHeight = newHeight;
            window.dispatchEvent(new Event('resize'));
          }
        }
      });
      this.formResizeObserver.observe(formEl);
    }
  }

  componentWillUnmount() {
    if (this.formResizeObserver) {
      this.formResizeObserver.disconnect();
      this.formResizeObserver = undefined;
    }
  }

  private toggleAPIKeyVisibility = (): void => {
    this.setState((prev) => ({ isAPIKeyVisible: !prev.isAPIKeyVisible }));
  };

  async handleSubmit(llmInfo: ConnectToLLMSetting, formik: FormikHelpers<ConnectToLLMSetting>) {
    formik.setSubmitting(true);

    // Gate submit for Custom Gateway when terms have not been accepted. This catches
    // EDIT mode where the dropdown is never touched (so handleProviderChange never fires).
    if (llmInfo.llmProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM && !llmInfo.termsAccepted) {
      this.openBetaTermsModalForSubmit(llmInfo, formik);
      formik.setSubmitting(false);
      return;
    }

    // Fail-closed in case a stale state slips past the disabled submit button.
    if (this.isOAuthSectionVisible(llmInfo.llmProvider, llmInfo.authMode) &&
        (this.state?.hasOAuthFileErrors || !this.state?.customOAuthConfigId)) {
      formik.setSubmitting(false);
      return;
    }

    // Copy validated OAuth file results onto llmInfo for the parent to save.
    if (this.isOAuthSectionVisible(llmInfo.llmProvider, llmInfo.authMode) &&
        this.state?.customOAuthConfigId) {
      llmInfo.oauthConfigId = this.state.customOAuthConfigId;
      llmInfo.configLabel = this.state.oauthConfigLabel || this.state.customOAuthConfigId;
      llmInfo.oauthConfigContent = this.state.oauthConfigContent;
      llmInfo.oauthConfigFileName = this.state.oauthFileName;
    }

    this.props.connectToLLMSubmit(llmInfo);
    formik.setSubmitting(false);
    this.props.close(true);
  }

  private handleProviderChange(value: string, formik: FormikProps<ConnectToLLMSetting>): void {
    if (value === PROVIDER_TYPES.BRING_YOUR_OWN_LLM && !formik.values.termsAccepted) {
      this.openBetaTermsModal(formik);
    }
  }

  private openBetaTermsModal(formik: FormikProps<ConnectToLLMSetting>): void {
    // openCustom injects { close, dismiss }; the dialog closes itself via close().
    ReactModalService.openCustom(({ close }) => (
      <BetaTermsDialog
        onAccept={() => {
          formik.setFieldValue('termsAccepted', true);
          sendByollmTermsAcceptedTelemetry();
          close();
        }}
        onCancel={() => {
          // Not accepted: back the dropdown out of Custom Gateway.
          formik.setFieldValue('llmProvider', PROVIDER_TYPES.SELECT_PROVIDER);
          close();
        }}
      />
    ));
  }

  private openBetaTermsModalForSubmit(llmInfo: ConnectToLLMSetting, formik: FormikHelpers<ConnectToLLMSetting>): void {
    // Opened from the submit path (not the dropdown). On cancel, do NOT reset the
    // provider — that would break edit mode where an existing BYOLLM site's provider
    // should not be changed just because the admin declined the terms modal.
    ReactModalService.openCustom(({ close }) => (
      <BetaTermsDialog
        onAccept={() => {
          llmInfo.termsAccepted = true;
          formik.setFieldValue('termsAccepted', true);
          sendByollmTermsAcceptedTelemetry();
          close();
          // Terms now accepted: proceed with the connect the admin already requested
          // instead of making them click Connect again.
          this.handleSubmit(llmInfo, formik);
        }}
        onCancel={() => {
          close();
        }}
      />
    ));
  }

  private isOAuthSectionVisible(provider: string, authMode?: AuthMode): boolean {
    return provider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM &&
           FeatureFlags.analyticsAssistantBYOLLMOAuthEnabled &&
           authMode === AUTH_MODES.OAUTH;
  }

  private isFormValidForProvider(formik: FormikProps<ConnectToLLMSetting>): boolean {
    const values = formik.values;
    const provider = values.llmProvider;

    if (!provider || provider === PROVIDER_TYPES.SELECT_PROVIDER) {
      return false;
    }

    if (provider === PROVIDER_TYPES.OPENAI) {
      return !!(values.providerAPIKey && values.providerAPIKey.trim() !== '');
    }

    if (provider === PROVIDER_TYPES.AZURE_OPENAI) {
      return !!(values.providerAPIKey && values.providerAPIKey.trim() !== '' &&
                values.azureResourceName && values.azureResourceName.trim() !== '');
    }

    if (provider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
      const hasHostURL = !!(values.hostURL && values.hostURL.trim() !== '');
      if (!hasHostURL) {
        return false;
      }

      const authMode = values.authMode || AUTH_MODES.NONE;

      if (authMode === AUTH_MODES.NONE) {
        return true;
      }

      if (authMode === AUTH_MODES.API_KEY) {
        return !!(values.providerAPIKey && values.providerAPIKey.trim() !== '');
      }

      if (authMode === AUTH_MODES.OAUTH) {
        const hasClientId = !!(values.oauthClientId && values.oauthClientId.trim() !== '');
        const hasClientSecret = !!(values.oauthClientSecret && values.oauthClientSecret.trim() !== '');
        const hasRedirectUri = !!(values.oauthRedirectUri && values.oauthRedirectUri.trim() !== '');

        if (!hasClientId || !hasClientSecret || !hasRedirectUri) {
          return false;
        }

        // Require an uploaded+validated OAuth config file.
        if (this.state?.validatingOAuthFile ||
            !this.state?.oauthFileName ||
            this.state?.hasOAuthFileErrors ||
            !this.state?.customOAuthConfigId) {
          return false;
        }

        return true;
      }

      return true;
    }

    return false;
  }

  private getDescription(learnMoreLink: string) {
    return <>
      <tr>
        <div id='connect-to-llm-dialog-description' className={ConnectToLLMDialogStyles.descriptionContainer}>
          <p className={ConnectToLLMDialogStyles.descriptionText}>{Messages.connectToLLMDialogSetUp()}</p>
          <TextLinkWidget
            className={learnMoreStyle}
            href={learnMoreLink}
            target='_blank'
            testId={'connect-to-llm-dialog-learn-more'}
          >
            {Messages.learnMore()}
          </TextLinkWidget>
        </div>
      </tr>
    </>;
  }

  private getLLMProviderDropdownSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element {
    const llmProviderOptions = [];

    llmProviderOptions.push(
      { value: PROVIDER_TYPES.SELECT_PROVIDER, content: Messages.connectToLLMDialogProviderSelectDefault(), testId: PROVIDER_TYPES.SELECT_PROVIDER }
    );

    llmProviderOptions.push(
      { value: PROVIDER_TYPES.OPENAI, content: Messages.connectToLLMDialogProviderOpenAI(), testId: PROVIDER_TYPES.OPENAI }
    );

    llmProviderOptions.push(
      { value: PROVIDER_TYPES.AZURE_OPENAI, content: Messages.connectToLLMDialogProviderAzureOpenAI(), testId: PROVIDER_TYPES.AZURE_OPENAI }
    );

    if (this.props.bringYourOwnLLMForOnPremEnabled) {
      llmProviderOptions.push(
        { separator: true } as any
      );
      llmProviderOptions.push(
        { value: PROVIDER_TYPES.BRING_YOUR_OWN_LLM, content: Messages.connectToLLMDialogProviderBringYourOwnLLM(), testId: PROVIDER_TYPES.BRING_YOUR_OWN_LLM }
      );
    }

    return (
      <tr>
        <td className='tb-settings-control'>
          <FormikSingleSelectDropdown
            name='llmProvider'
            list={<List items={llmProviderOptions} />}
            type={DropdownButtonType.Outline}
            label={Messages.connectToLLMDialogProviderLabel()}
            className={ConnectToLLMDialogStyles.inputStyle}
            testId={'connect-to-llm-dialog-choose-llmProvider'}
            value={formik.values.llmProvider}
            onValueChange={(value: string) => this.handleProviderChange(value, formik)}
          />
        </td>
      </tr>
    );
  }

  private getAuthModeDropdownSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (formik.values.llmProvider !== PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
      return null;
    }

    const authModeOptions: Array<{ value: AuthMode, content: string, testId: AuthMode }> = [
      { value: AUTH_MODES.NONE, content: Messages.connectToLLMDialogAuthModeNone(), testId: AUTH_MODES.NONE },
      { value: AUTH_MODES.API_KEY, content: Messages.connectToLLMDialogAuthModeApiKey(), testId: AUTH_MODES.API_KEY },
    ];
    // OAuth is gated behind a feature flag — only offer it when enabled.
    if (FeatureFlags.analyticsAssistantBYOLLMOAuthEnabled) {
      authModeOptions.push(
        { value: AUTH_MODES.OAUTH, content: Messages.connectToLLMDialogAuthModeOAuth(), testId: AUTH_MODES.OAUTH },
      );
    }

    return (
      <tr>
        <td className='tb-settings-control'>
          <FormikSingleSelectDropdown
            name='authMode'
            list={<List items={authModeOptions} />}
            type={DropdownButtonType.Outline}
            label={Messages.connectToLLMDialogAuthModeLabel()}
            className={ConnectToLLMDialogStyles.inputStyle}
            testId={'connect-to-llm-dialog-choose-authMode'}
            value={formik.values.authMode || AUTH_MODES.NONE}
            disabled={formik.isSubmitting}
          />
        </td>
      </tr>
    );
  }

  private getAPIKeyInputSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    // Don't show API key field when no provider is selected
    if (formik.values.llmProvider === PROVIDER_TYPES.SELECT_PROVIDER) {
      return null;
    }

    // For Custom Gateway, only show API key field when authMode === ApiKey.
    if (formik.values.llmProvider === PROVIDER_TYPES.BRING_YOUR_OWN_LLM
        && formik.values.authMode !== AUTH_MODES.API_KEY) {
      return null;
    }

    const label = this.props.isEditing ? Messages.connectToLLMDialogChangeAPIKeyLabel() : Messages.connectToLLMDialogAPIKeyLabel();
    const maskEnabled = FeatureFlags.maskLLMAPIKeyInput;
    const isVisible = this.state.isAPIKeyVisible;

    const textBox = (
      <FormikTextBox
        name='providerAPIKey'
        testId='providerAPIKey'
        label={label}
        className={maskEnabled ? ConnectToLLMDialogStyles.apiKeyInputField : ConnectToLLMDialogStyles.inputStyle}
        type={maskEnabled && !isVisible ? 'password' : 'text'}
      />
    );

    let content: JSX.Element = textBox;
    if (maskEnabled) {
      const toggleLabel = isVisible
        ? Messages.connectToLLMDialogHideAPIKey()
        : Messages.connectToLLMDialogShowAPIKey();
      content = (
        <div className={ConnectToLLMDialogStyles.apiKeyInputContainer}>
          {textBox}
          <button
            type='button'
            className={ConnectToLLMDialogStyles.apiKeyVisibilityToggle}
            data-tb-test-id='providerAPIKey-visibility-toggle'
            onClick={this.toggleAPIKeyVisibility}
            title={toggleLabel}
            aria-label={toggleLabel}
            aria-pressed={isVisible}
          >
            {isVisible
              ? <HideBaseIcon size={ConnectToLLMDialogStyles.API_KEY_TOGGLE_ICON_SIZE} />
              : <ShowBaseIcon size={ConnectToLLMDialogStyles.API_KEY_TOGGLE_ICON_SIZE} />}
          </button>
        </div>
      );
    }

    return (
      <tr>
        <td className='tb-settings-control'>
          {content}
        </td>
      </tr>
    );
  }

  private getAzureOpenAIInputSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element[] {
    if (formik.values.llmProvider !== PROVIDER_TYPES.AZURE_OPENAI) {
      return [];
    }

    const fields: JSX.Element[] = [];

    // Always show resource name
    fields.push(
      <tr key="azure-resource-name">
        <td className='tb-settings-control'>
          <FormikTextBox
            name='azureResourceName'
            testId='azureResourceName'
            label={Messages.connectToLLMDialogAzureResourceNameLabel()}
            className={ConnectToLLMDialogStyles.inputStyle}
          />
        </td>
      </tr>
    );

    // Show additional fields when SP2 is enabled
    if (this.props.azureAiOnPremEnabledSP2) {
      fields.push(
        <tr key="azure-subscription-id">
          <td className='tb-settings-control'>
            <FormikTextBox
              name='azureSubscriptionId'
              testId='azureSubscriptionId'
              label={Messages.connectToLLMDialogAzureSubscriptionIdLabel()}
              className={ConnectToLLMDialogStyles.inputStyle}
            />
          </td>
        </tr>,
        <tr key="azure-resource-group">
          <td className='tb-settings-control'>
            <FormikTextBox
              name='azureResourceGroup'
              testId='azureResourceGroup'
              label={Messages.connectToLLMDialogAzureResourceGroupLabel()}
              className={ConnectToLLMDialogStyles.inputStyle}
            />
          </td>
        </tr>,
        <tr key="azure-tenant-id">
          <td className='tb-settings-control'>
            <FormikTextBox
              name='azureTenantId'
              testId='azureTenantId'
              label={Messages.connectToLLMDialogAzureTenantIdLabel()}
              className={ConnectToLLMDialogStyles.inputStyle}
            />
          </td>
        </tr>,
        <tr key="azure-service-principal-secret">
          <td className='tb-settings-control'>
            <FormikTextBox
              name='azureServicePrincipalSecret'
              testId='azureServicePrincipalSecret'
              label={Messages.connectToLLMDialogAzureServicePrincipalSecretLabel()}
              className={ConnectToLLMDialogStyles.inputStyle}
            />
          </td>
        </tr>
      );
    }

    return fields;
  }

  private getBringYourOwnLLMInputSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (formik.values.llmProvider !== PROVIDER_TYPES.BRING_YOUR_OWN_LLM) {
      return null;
    }

    return (
      <tr>
        <td className='tb-settings-control'>
          <FormikTextBox
            name='hostURL'
            testId='hostURL'
            label={Messages.connectToLLMDialogHostURLLabel()}
            className={ConnectToLLMDialogStyles.inputStyle}
          />
        </td>
      </tr>
    );
  }

  // --- OAuth Section for Custom Gateway (LLM Gateway) ---

  private getOAuthSectionHeader(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (!this.isOAuthSectionVisible(formik.values.llmProvider, formik.values.authMode)) {
      return null;
    }

    return (
      <tr key="oauth-section-header">
        <td className='tb-settings-control'>
          <div className={ConnectToLLMDialogStyles.oauthSectionHeader}>
            <b>{Messages.oauthClientDialogTitle()}</b>
          </div>
        </td>
      </tr>
    );
  }

  private getOAuthClientIdSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (!this.isOAuthSectionVisible(formik.values.llmProvider, formik.values.authMode)) {
      return null;
    }

    return (
      <tr key="oauth-client-id">
        <td className='tb-settings-control'>
          <FormikTextBox
            name='oauthClientId'
            testId='llm-oauthClientId'
            label={Messages.oauthClientId()}
            className={ConnectToLLMDialogStyles.inputStyle}
            validate={validateNoEmbeddedWhiteSpaceCI}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getOAuthClientSecretSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (!this.isOAuthSectionVisible(formik.values.llmProvider, formik.values.authMode)) {
      return null;
    }

    return (
      <tr key="oauth-client-secret">
        <td className='tb-settings-control'>
          <FormikTextBox
            name='oauthClientSecret'
            testId='llm-oauthClientSecret'
            label={Messages.oauthClientSecret()}
            className={ConnectToLLMDialogStyles.inputStyle}
            validate={validateNoEmbeddedWhiteSpaceCS}
            showValidationMessage={true}
            type='password'
          />
        </td>
      </tr>
    );
  }

  private getOAuthRedirectUriSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (!this.isOAuthSectionVisible(formik.values.llmProvider, formik.values.authMode)) {
      return null;
    }

    return (
      <tr key="oauth-redirect-uri">
        <td className='tb-settings-control'>
          <FormikTextBox
            name='oauthRedirectUri'
            testId='llm-oauthRedirectUri'
            label={Messages.oauthRedirectUri()}
            className={ConnectToLLMDialogStyles.inputStyle}
            validate={validateRedirectUri}
            showValidationMessage={true}
          />
        </td>
      </tr>
    );
  }

  private getOAuthConfigFileSection(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (!this.isOAuthSectionVisible(formik.values.llmProvider, formik.values.authMode)) {
      return null;
    }

    const fileStatus = this.state?.oauthFileName
      ? (this.state?.hasOAuthFileErrors
        ? ConnectToLLMDialogStyles.invalidFileColor
        : ConnectToLLMDialogStyles.validFileColor)
      : ConnectToLLMDialogStyles.defaultFileColor;

    return (
      <tr key="oauth-config-file" data-tb-test-id="llm-oauth-file-upload">
        <td className='tb-settings-control'>
          <label>{Messages.oauthClientConfigContent()}</label>
          <div className={`${ConnectToLLMDialogStyles.fileSelectionContainer} ${fileStatus}`}>
            <FileInput
              accept={ACCEPTED_FILE_TYPES.join(',')}
              handleFileInput={(fileUploaded) => this.handleOAuthFileSelection(fileUploaded, formik)}
              name='oauthConfigFile'
              onClick={this.resetUploadedFile}
              ref={this.fileInput}
              testId='llm-oauth-config-file-input'
            />
            <div className={ConnectToLLMDialogStyles.dragAndDropChildren}>
              {!this.state?.validatingOAuthFile && this.state?.oauthFileName &&
                <b className={`${ConnectToLLMDialogStyles.textMargin} ${fileStatus}`}>
                  {this.state.oauthFileName}
                </b>
              }
              {this.state?.validatingOAuthFile &&
                <b className={ConnectToLLMDialogStyles.spinnerStyle}>
                  <ActivitySpinnerWidget testId={'llm-oauth-file-upload-spinner'} shouldShowUnderlay={false} />
                </b>
              }
              <ButtonWidget
                buttonType={ButtonType.Outline}
                type="button"
                testId="llm-oauth-upload-xml-file"
                children={Messages.contentUpload_chooseAFile()}
                handleClick={this.triggerFileInput}
                disabled={formik.isSubmitting}
              />
            </div>
          </div>
        </td>
      </tr>
    );
  }

  private getOAuthErrorBanner(formik: FormikProps<ConnectToLLMSetting>): JSX.Element | null {
    if (!this.isOAuthSectionVisible(formik.values.llmProvider, formik.values.authMode)) {
      return null;
    }

    if (!this.state?.oauthFileName || !this.state?.hasOAuthFileErrors) {
      return null;
    }

    return (
      <tr key="oauth-error-banner" data-tb-test-id="llm-oauth-error-banner">
        <td className='tb-settings-control'>
          <ListDialogErrorAlert
            testId={'llm-oauth-invalid-config-alert'}
            message={Messages.invalidOAuthConfigFile()}
            errorListTitle={Messages.addNewSavedCredentialServiceAccountInvalidFile_WhatToTryNext()}
            errors={[Messages.validateCustomOAuthConfigFailure_LogDetails(), Messages.validateCustomOAuthConfigFailure_SdkDocs()]}
          />
        </td>
      </tr>
    );
  }

  private resetUploadedFile = async (event: React.MouseEvent<HTMLInputElement, MouseEvent>) => {
    const element = event.target as HTMLInputElement;
    element.value = '';
  };

  private handleOAuthFileSelection = async (fileUploaded: File | undefined, formik: FormikProps<ConnectToLLMSetting>) => {
    if (fileUploaded) {
      this.setState({ validatingOAuthFile: true });

      let hasFileErrors = false;
      let configId = '';
      let configLabel = '';
      let configContent = '';
      const dbclass = OAuthCredentialTypes.LlmGateway;

      // Gate on extension: some browsers report application/xml or an empty
      // MIME type for .xml files. Server-side validation is the real gate.
      const hasXmlExtension = ACCEPTED_FILE_TYPES.some(
        (ext) => fileUploaded.name.toLowerCase().endsWith(ext),
      );

      try {
        if (!hasXmlExtension || fileUploaded.size > FILE_SIZE_LIMIT) {
          hasFileErrors = true;
        } else {
          const content = await readFileContent(fileUploaded);
          const response = await validateCustomOAuthConfig(content, dbclass);

          if (response.errors) {
            hasFileErrors = true;
          } else {
            configId = response.customOAuthConfigId;
            configLabel = response.configLabel;
            configContent = content;
          }
        }
      } catch (e) {
        // Surface FileReader / HTTP rejections via the error banner instead
        // of leaving the spinner stuck with an unhandled rejection.
        hasFileErrors = true;
        Tableau.Logger().warn('OAuth config validation failed: ' + e);
      } finally {
        this.setState({
          oauthFileName: fileUploaded.name,
          oauthConfigContent: configContent,
          customOAuthConfigId: configId,
          oauthConfigLabel: configLabel,
          hasOAuthFileErrors: hasFileErrors,
          validatingOAuthFile: false,
        });
      }
    }
  };

  private triggerFileInput = () => {
    this.fileInput.current.click();
  };

  public render() {
    const defaultProvider = PROVIDER_TYPES.SELECT_PROVIDER;
    const initialValues: ConnectToLLMSetting = {
      llmProvider: this.props.currentValues?.llmProvider || defaultProvider,
      providerAPIKey: this.props.currentValues?.providerAPIKey || '',
      azureResourceName: this.props.currentValues?.azureResourceName || '',
      azureSubscriptionId: this.props.currentValues?.azureSubscriptionId || '',
      azureResourceGroup: this.props.currentValues?.azureResourceGroup || '',
      azureTenantId: this.props.currentValues?.azureTenantId || '',
      azureServicePrincipalSecret: this.props.currentValues?.azureServicePrincipalSecret || '',
      hostURL: this.props.currentValues?.hostURL || '',
      authMode: this.props.currentValues?.authMode || AUTH_MODES.NONE,
      // OAuth initial values
      oauthConfigId: this.props.currentValues?.oauthConfigId || 'default',
      oauthClientId: this.props.currentValues?.oauthClientId || '',
      oauthClientSecret: this.props.currentValues?.oauthClientSecret || '',
      oauthRedirectUri: this.props.currentValues?.oauthRedirectUri || '',
      oauthConfigContent: this.props.currentValues?.oauthConfigContent || '',
      oauthConfigFileName: this.props.currentValues?.oauthConfigFileName || '',
      configLabel: this.props.currentValues?.configLabel || '',
      termsAccepted: this.props.currentValues?.termsAccepted || false,
    };
    const connectToLLMDialogLearnMoreLink = HelpService.getHelpUrlByCategory('gpt-server-models');
    return (
      <Formik
        initialValues={initialValues}
        onSubmit={(values: ConnectToLLMSetting, formik: FormikHelpers<ConnectToLLMSetting>) => {
          this.handleSubmit(values, formik);
        }}
      >
        {(formik: FormikProps<ConnectToLLMSetting>) => (
          <form ref={this.formRef} onSubmit={formik.handleSubmit} data-tb-test-id='connect-to-llm-dialog'>
            <table className={`tb-form-table ${ConnectToLLMDialogStyles.formTable}`}>
              <tbody>
                {this.getDescription(connectToLLMDialogLearnMoreLink)}
                {this.getLLMProviderDropdownSection(formik)}
                {this.getBringYourOwnLLMInputSection(formik)}
                {this.getAuthModeDropdownSection(formik)}
                {this.getAPIKeyInputSection(formik)}
                {this.getAzureOpenAIInputSection(formik)}
                {this.getOAuthSectionHeader(formik)}
                {this.getOAuthClientIdSection(formik)}
                {this.getOAuthClientSecretSection(formik)}
                {this.getOAuthRedirectUriSection(formik)}
                {this.getOAuthConfigFileSection(formik)}
                {this.getOAuthErrorBanner(formik)}
              </tbody>
            </table>
            <div className='tb-dialog-actions'>
              <ButtonWidget
                buttonType={ButtonType.Outline}
                testId='connect-to-llm-dialog-dismiss'
                onClick={() => this.props.dismiss()}
                disabled={formik.isSubmitting}
                type='button'
              >
                {Messages.cancel()}
              </ButtonWidget>
              <ButtonWidget
                buttonType={ButtonType.Primary}
                testId='connect-to-llm-dialog-connect-button'
                className='tb-margin-left-wide'
                disabled={!this.isFormValidForProvider(formik) || formik.isSubmitting}
                type='submit'
              >
                {Messages.connectToLLMDialogConnectButton()}
              </ButtonWidget>
            </div>
          </form>
        )}
      </Formik>
    );
  }
}
